import { useMatch, useRouteError } from 'react-router-dom'
import styled, { css } from 'styled-components'
import React, { useEffect, useState } from 'react'
import message_icon from 'src/assets/images/icon/icon_message.png'
import offline_icon from 'src/assets/images/icon/icon_offline.png'
import { Noto_body1_r16 } from 'src/styles/fonts/NotoSansKrFonts'
import Button, { ButtonOption1, ButtonOption2 } from 'src/components/common/Button'
import { Container } from 'src/components/common/Container'
import usePageNav from 'src/hooks/usePageNav.hook'
import RenderIf from 'src/components/common/RenderIf'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { captureError } from 'src/utils/sentry'

const ErrorBoundaryPage = () => {
  const error = useRouteError()
  const { splashPageNav, navigatePrev, mainPageNav } = usePageNav()
  const { channel } = useChannel()
  const [online, setOnline] = useState(true)
  const hotdealMatch = useMatch(`/${channel}/hotdeal`)

  // console.warn('error', error)

  useEffect(() => {
    //todo v1: hotdeal => v2: main, 앱측 코드가 수정된다면 제거해야할 코드
    if (hotdealMatch) {
      splashPageNav()
    }
    if ((error as any)?.code === 'ERR_NETWORK' || !navigator.onLine) {
      setOnline(false)
    } else {
      setOnline(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!error) return
    captureError(error)
  }, [error])

  useEffect(() => {
    if (!online) {
      const checkNetworkAndReload = async (retryCount = 0) => {
        const currentPath = window.location.origin
        let serverUrl

        switch (true) {
          case currentPath.includes('5.0'):
          case currentPath.includes('localhost'):
          case currentPath.includes('dev-svc'):
            serverUrl = 'https://dev-mcrs.couponwallet.co.kr'
            break
          default:
            serverUrl = 'https://mcrs.couponwallet.co.kr'
        }

        try {
          const response = await fetch(serverUrl)
          // 유효한 응답을 받으면 페이지를 재로드
          if (response.status >= 200 && response.status < 500) {
            window.location.reload()
            return
          }
        } catch {
          // 서버에 연결할 수 없으면 무시.
        }
        // 5번 실행 후 종료
        if (retryCount < 4) {
          window.setTimeout(() => checkNetworkAndReload(retryCount + 1), 1000 * 5)
        }
      }

      console.warn('offline mode')
      checkNetworkAndReload?.()
    }
  }, [online])

  const OnlineErrorPage = () => {
    return (
      <ErrorBoundaryWrap>
        <ErrorContents>
          <ErrorMessageIcon src={message_icon} alt={'메시지 아이콘'} />
          <ErrorText>페이지를 찾을 수 없습니다.</ErrorText>
          <Container $option="flex_column" $style={ErrorButtonBox}>
            <Button option={ButtonOption1('default')} onClickFn={navigatePrev}>
              뒤로가기
            </Button>
            <Button option={ButtonOption2('default')} onClickFn={mainPageNav}>
              홈으로
            </Button>
          </Container>
        </ErrorContents>
      </ErrorBoundaryWrap>
    )
  }

  const OfflineErrorPage = () => {
    return (
      <div className="offline-flex-container">
        <div className="offline-flex-contents">
          <img src={offline_icon} alt="icon" className="offline-wifi-off-image" />
          <p className="offline-primary-text">네트워크가 불안정합니다.</p>
          <div className="offline-button-container">
            <button className="offline-refresh-button" onClick={() => window.location.reload()}>
              새로고침
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      <RenderIf when={online}>
        <OnlineErrorPage />
      </RenderIf>
      <RenderIf when={!online}>
        <OfflineErrorPage />
      </RenderIf>
    </React.Fragment>
  )
}

export default ErrorBoundaryPage

const ErrorBoundaryWrap = styled.div`
  height: calc(var(--vh, 1vh) * 100);
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const ErrorContents = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 320px;
  width: 100%;
  margin: 0 auto;
`

const ErrorMessageIcon = styled.img`
  width: 40px;
  height: 30px;
  margin: 0 auto;
`

const ErrorText = styled.p`
  ${Noto_body1_r16};
  margin-top: 28px;
  margin-bottom: 70px;
  text-align: center;
  color: var(--black-50);
`

const ErrorButtonBox = css`
  gap: 10px;
  padding: 0 30px;
`
